import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, User, Package, Menu, X } from "lucide-react";

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogout = () => { logout(); setDropOpen(false); setMenuOpen(false); navigate("/"); };

  const navLinks = [["how","How it works"],["api","API"],["pricing","Pricing"],["order","Quick Delivery"]];

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 6%",
        background: scrolled || menuOpen ? "rgba(9,9,14,0.98)" : "rgba(9,9,14,0.6)",
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${scrolled ? "var(--border)" : "transparent"}`,
        transition: "background 0.3s, border-color 0.3s",
      }}>
        {/* Logo */}
        <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: "flex", alignItems: "center", zIndex: 201 }}>
          <img src="/logo.png" alt="Kwikie Express" style={{ height: 42, width: "auto", display: "block" }} />
        </Link>

        {/* Desktop links */}
        <div style={{ display: "flex", alignItems: "center", gap: 28 }} className="hide-mobile">
          {navLinks.map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)}
              style={{ background:"none", border:"none", color:"var(--text2)", fontSize:14, fontWeight:500, cursor:"pointer", fontFamily:"var(--font-body)", transition:"color 0.2s" }}
              onMouseOver={e=>e.target.style.color="var(--text)"} onMouseOut={e=>e.target.style.color="var(--text2)"}>
              {label}
            </button>
          ))}
          {/* Invest link — accent coloured to stand out */}
          <Link
            to="/invest"
            style={{ fontSize:14, fontWeight:700, color:"var(--accent)", fontFamily:"var(--font-body)", textDecoration:"none", transition:"opacity 0.2s" }}
            onMouseOver={e=>e.currentTarget.style.opacity="0.75"} onMouseOut={e=>e.currentTarget.style.opacity="1"}
          >
            💰 Invest
          </Link>
        </div>

        {/* Desktop right side */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }} className="hide-mobile">
          {user ? (
            <div style={{ position: "relative" }}>
              <button onClick={() => setDropOpen(p => !p)} style={{
                display:"flex", alignItems:"center", gap:8,
                background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8,
                padding:"8px 14px", color:"var(--text)", fontSize:13, fontWeight:600, cursor:"pointer",
              }}>
                <User size={14}/> {user.name?.split(" ")[0]}
              </button>
              {dropOpen && (
                <div style={{
                  position:"absolute", top:"calc(100% + 8px)", right:0, minWidth:180,
                  background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10,
                  overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.4)",
                }}>
                  <Link to="/orders" onClick={() => setDropOpen(false)} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", fontSize:13, color:"var(--text2)", borderBottom:"1px solid var(--border)", textDecoration:"none" }}
                    onMouseOver={e=>e.currentTarget.style.background="var(--bg4)"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                    <Package size={14}/> My Orders
                  </Link>
                  <button onClick={handleLogout} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", fontSize:13, color:"var(--red)", background:"none", border:"none", width:"100%", cursor:"pointer" }}
                    onMouseOver={e=>e.currentTarget.style.background="rgba(239,68,68,0.08)"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                    <LogOut size={14}/> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="btn-ghost btn-sm">Sign in</Link>
          )}
          <Link to="/developer" style={{ fontSize:13, color:"var(--text2)", fontWeight:500, transition:"color 0.2s" }}
            onMouseOver={e=>e.currentTarget.style.color="var(--text)"} onMouseOut={e=>e.currentTarget.style.color="var(--text2)"}>
            Dev Portal
          </Link>
          <button className="btn-primary btn-sm" onClick={() => scrollTo("order")}>⚡ Deliver Now</button>
        </div>

        {/* Mobile quick actions — visible before hamburger */}
        <div className="show-mobile" style={{ display:"none", alignItems:"center", gap:8, zIndex:201 }}>
          <Link
            to="/invest"
            onClick={() => setMenuOpen(false)}
            style={{ fontSize:12, fontWeight:800, color:"#000", background:"var(--accent)", padding:"6px 11px", borderRadius:7, textDecoration:"none", whiteSpace:"nowrap" }}
          >
            💰 Invest
          </Link>
          {user ? (
            <Link to="/orders" onClick={() => setMenuOpen(false)}
              style={{ fontSize:12, fontWeight:600, color:"var(--text2)", background:"var(--bg3)", border:"1px solid var(--border)", padding:"6px 11px", borderRadius:7, textDecoration:"none", display:"flex", alignItems:"center", gap:5 }}>
              <User size={12}/> {user.name?.split(" ")[0]}
            </Link>
          ) : (
            <Link to="/login" onClick={() => setMenuOpen(false)}
              style={{ fontSize:12, fontWeight:600, color:"var(--text2)", background:"var(--bg3)", border:"1px solid var(--border)", padding:"6px 11px", borderRadius:7, textDecoration:"none" }}>
              Sign in
            </Link>
          )}
          <Link to="/developer" onClick={() => setMenuOpen(false)}
            style={{ fontSize:12, fontWeight:600, color:"var(--text2)", background:"var(--bg3)", border:"1px solid var(--border)", padding:"6px 11px", borderRadius:7, textDecoration:"none", whiteSpace:"nowrap" }}>
            Dev
          </Link>
          <button
            onClick={() => setMenuOpen(p => !p)}
            style={{ background:"none", border:"none", color:"var(--text)", cursor:"pointer", padding:4, marginLeft:2 }}
          >
            {menuOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 190,
        background: "rgba(9,9,14,0.98)",
        backdropFilter: "blur(20px)",
        display: "flex", flexDirection: "column",
        padding: "90px 6% 40px",
        transform: menuOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Mobile nav links */}
        <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:32 }}>
          {navLinks.map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)}
              style={{ background:"none", border:"none", color:"var(--text)", fontSize:22, fontWeight:700, cursor:"pointer", fontFamily:"var(--font-head)", textAlign:"left", padding:"12px 0", borderBottom:"1px solid var(--border)" }}>
              {label}
            </button>
          ))}
          {/* Invest — mobile */}
          <Link to="/invest" onClick={() => setMenuOpen(false)}
            style={{ color:"var(--accent)", fontSize:22, fontWeight:800, fontFamily:"var(--font-head)", padding:"12px 0", borderBottom:"1px solid var(--border)", textDecoration:"none" }}>
            💰 Invest
          </Link>
          <Link to="/developer" onClick={() => setMenuOpen(false)}
            style={{ color:"var(--text2)", fontSize:22, fontWeight:700, fontFamily:"var(--font-head)", padding:"12px 0", borderBottom:"1px solid var(--border)", textDecoration:"none" }}>
            Dev Portal
          </Link>
        </div>

        {/* Mobile auth */}
        <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:"auto" }}>
          {user ? (
            <>
              <Link to="/orders" onClick={() => setMenuOpen(false)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", fontSize:14, color:"var(--text)", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, textDecoration:"none", fontWeight:600 }}>
                <Package size={16}/> My Orders
              </Link>
              <button onClick={handleLogout}
                style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"14px 16px", fontSize:14, color:"var(--red)", background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:10, cursor:"pointer", fontWeight:600 }}>
                <LogOut size={16}/> Sign out
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMenuOpen(false)}
              style={{ textAlign:"center", padding:"14px", fontSize:14, fontWeight:700, color:"var(--text)", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, textDecoration:"none" }}>
              Sign in
            </Link>
          )}
          <button className="btn-primary" onClick={() => scrollTo("order")} style={{ width:"100%", padding:"14px", fontSize:15, fontWeight:700 }}>
            ⚡ Deliver Now
          </button>
        </div>
      </div>

      {/* Global mobile styles */}
      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
        }
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
        }
      `}</style>
    </>
  );
}